import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DesktopShell } from '../components/DesktopShell';
import { TopBar } from '../components/TopBar';
import { Card } from '../components/Card';
import { Btn } from '../components/Btn';
import { Ic } from '../components/Icon';
import { EmojiPickerButton } from '../components/EmojiPicker';
import { roomsApi } from '../api/rooms';

export function CreateRoomPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [type, setType] = useState<'PRIVATE' | 'PUBLIC'>('PRIVATE');
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('🏆');
  const [description, setDescription] = useState('');
  const [maxMembers, setMaxMembers] = useState(50);
  const [membersCanInvite, setMembersCanInvite] = useState(true);

  const create = useMutation({
    mutationFn: () =>
      roomsApi.create({
        name,
        emoji,
        description: description || undefined,
        type,
        maxMembers,
        membersCanInvite,
      }),
    onSuccess: async (room) => {
      await queryClient.invalidateQueries({ queryKey: ['my-rooms'] });
      navigate(`/rooms/${room.id}`);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) create.mutate();
  };

  return (
    <DesktopShell>
      <TopBar title="Nowy pokój" subtitle="Stwórz przestrzeń dla swojej ekipy" />
      <form
        onSubmit={onSubmit}
        className="stack-on-mobile"
        style={{ flex: 1, padding: '28px 36px', display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 24 }}
      >
        <Card padding={28}>
          <div style={{ fontSize: 11, color: '#5b6578', letterSpacing: '.06em', fontWeight: 600, marginBottom: 10 }}>
            TYP POKOJU
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12, marginBottom: 28 }}>
            <TypeOption
              active={type === 'PRIVATE'}
              onClick={() => setType('PRIVATE')}
              icon={<Ic.lock size={18} />}
              label="Prywatny"
              desc="Tylko na zaproszenie. Niewidoczny w wyszukiwarce."
            />
            <TypeOption
              active={type === 'PUBLIC'}
              onClick={() => setType('PUBLIC')}
              icon={<Ic.globe size={18} />}
              label="Publiczny"
              desc="Każdy może poprosić o dołączenie. Wy akceptujecie."
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 12, marginBottom: 18 }}>
            <div>
              <FieldLabel>IKONA</FieldLabel>
              <EmojiPickerButton value={emoji} onChange={setEmoji} size={48} style={{ width: '100%' }} />
            </div>
            <div>
              <FieldLabel>NAZWA POKOJU</FieldLabel>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Żubry 2026"
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: 12,
                  border: '1px solid rgba(15,30,61,.12)',
                  padding: '0 14px',
                  fontSize: 15,
                  outline: 'none',
                  background: '#fff',
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 22 }}>
            <FieldLabel>OPIS (OPCJONALNIE)</FieldLabel>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ekipa ze Szkoły Podstawowej 38 — typowanie mundialu i czasem po ligawa."
              style={{
                width: '100%',
                height: 72,
                borderRadius: 12,
                border: '1px solid rgba(15,30,61,.12)',
                padding: '10px 14px',
                fontSize: 14,
                resize: 'none',
                outline: 'none',
                background: '#fff',
              }}
            />
          </div>

          <div style={{ fontSize: 11, color: '#5b6578', letterSpacing: '.06em', fontWeight: 600, marginBottom: 10 }}>
            USTAWIENIA
          </div>

          <SettingRow title="Limit członków" hint="Maksymalna liczba osób w pokoju">
            <input
              type="number"
              min={1}
              max={1000}
              value={maxMembers}
              onChange={(e) => setMaxMembers(Number(e.target.value))}
              style={{
                width: 80,
                height: 32,
                borderRadius: 8,
                border: '1px solid rgba(15,30,61,.12)',
                padding: '0 10px',
                fontSize: 13,
                textAlign: 'right',
                background: '#fff',
                outline: 'none',
              }}
            />
          </SettingRow>

          <SettingRow
            title="Członkowie mogą zapraszać"
            hint="Uczestnicy wysyłają zaproszenia — akceptuje tylko właściciel"
          >
            <Toggle on={membersCanInvite} onChange={setMembersCanInvite} />
          </SettingRow>

          {create.isError && (
            <div style={{ marginTop: 12, padding: 12, background: 'rgba(208,70,60,.08)', color: '#8a2a22', borderRadius: 10, fontSize: 13 }}>
              Nie udało się utworzyć pokoju.
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            <Btn type="button" variant="ghost" size="lg" style={{ flex: 1 }} onClick={() => navigate(-1)}>
              Anuluj
            </Btn>
            <Btn
              type="submit"
              variant="accent"
              size="lg"
              style={{ flex: 2 }}
              disabled={!name.trim() || create.isPending}
            >
              {create.isPending ? 'Tworzenie…' : 'Utwórz pokój'}
            </Btn>
          </div>
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card padding={0} style={{ overflow: 'hidden' }}>
            <div style={{ background: '#0f1e3d', color: '#fff', padding: '22px 24px', position: 'relative' }}>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--accent)',
                  letterSpacing: '.08em',
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                PODGLĄD
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 12,
                    background: 'rgba(255,255,255,.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 26,
                  }}
                >
                  {emoji}
                </div>
                <div>
                  <div className="serif" style={{ fontSize: 22, fontWeight: 700 }}>
                    {name || 'Nowy pokój'}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'rgba(255,255,255,.6)',
                      marginTop: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {type === 'PRIVATE' ? (
                      <>
                        <Ic.lock size={11} /> Prywatny pokój
                      </>
                    ) : (
                      <>
                        <Ic.globe size={11} /> Publiczny pokój
                      </>
                    )}{' '}
                    · limit {maxMembers}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ padding: 22 }}>
              <div style={{ fontSize: 11, color: '#5b6578', letterSpacing: '.06em', fontWeight: 600, marginBottom: 12 }}>
                CO DALEJ
              </div>
              {[
                { n: 1, t: 'Utwórz pokój', s: 'Po utworzeniu staniesz się właścicielem.', active: true },
                { n: 2, t: 'Zaproś ekipę', s: 'Linki i e-maile wyślesz z zakładki "Zarządzaj pokojem".' },
                { n: 3, t: 'Obstawiajcie razem', s: 'Mecze startują 11 czerwca 2026.' },
              ].map((s) => (
                <div
                  key={s.n}
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: '10px 0',
                    borderTop: s.n === 1 ? 'none' : '1px solid rgba(15,30,61,.05)',
                  }}
                >
                  <div
                    className="serif num"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: s.active ? 'var(--accent)' : '#f4f3ee',
                      color: s.active ? '#3a2a00' : '#5b6578',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {s.n}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: s.active ? '#0f1e3d' : '#5b6578' }}>
                      {s.t}
                    </div>
                    <div style={{ fontSize: 11.5, color: '#5b6578', marginTop: 2, lineHeight: 1.4 }}>{s.s}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </form>
    </DesktopShell>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, color: '#5b6578', fontWeight: 600, marginBottom: 6, letterSpacing: '.04em' }}>
      {children}
    </div>
  );
}

function TypeOption({
  active,
  onClick,
  icon,
  label,
  desc,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '18px 16px',
        borderRadius: 12,
        cursor: 'pointer',
        textAlign: 'left',
        border: '1.5px solid',
        borderColor: active ? '#0f1e3d' : 'rgba(15,30,61,.12)',
        background: active ? 'rgba(15,30,61,.03)' : '#fff',
        fontFamily: 'inherit',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: active ? 'var(--accent)' : '#f4f3ee',
            color: active ? '#3a2a00' : '#5b6578',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </div>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{label}</div>
        {active && <Ic.check size={14} style={{ marginLeft: 'auto', color: '#0f1e3d' }} />}
      </div>
      <div style={{ fontSize: 12, color: '#5b6578', lineHeight: 1.45 }}>{desc}</div>
    </button>
  );
}

function SettingRow({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 0',
        borderTop: '1px solid rgba(15,30,61,.06)',
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 13.5 }}>{title}</div>
        <div style={{ fontSize: 11.5, color: '#5b6578', marginTop: 2 }}>{hint}</div>
      </div>
      {children}
    </div>
  );
}

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      style={{
        width: 36,
        height: 22,
        borderRadius: 999,
        background: on ? 'var(--accent)' : 'rgba(15,30,61,.18)',
        position: 'relative',
        cursor: 'pointer',
        border: 'none',
        padding: 0,
        transition: 'background .15s',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 16 : 2,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,.2)',
          transition: 'left .15s',
        }}
      />
    </button>
  );
}
